import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Stethoscope, Trash2, Plus, MapPin, PencilLine } from "lucide-react";

const RADII = [25, 50, 100];
const CHIP = {
  in_network: "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]",
  needs_verification: "bg-[#F3F4F6] text-[#374151] border-[#D1D5DB]",
};
const kOf = (d) => `${d.doctor_key || d.practitioner_id}|${d.location_key || d.location_id}`;

function Coverage({ res }) {
  return (
    <div className={`mt-2 rounded-lg p-2.5 text-xs ${res.complete ? "bg-white border border-[#E5E2DA] text-[#5B6670]" : "bg-[#FEF3C7] border border-[#F59E0B]/40 text-[#92400E]"}`} data-testid={res.complete ? "mydoc-search-complete" : "mydoc-search-incomplete"}>
      {res.coverage.map((c) => (
        <div key={c.source} data-testid={`mydoc-coverage-${c.source}`}>
          {c.label}: {c.reason === "no_verified_2027_plans" ? "2027 plan networks not found in the directory we check — its plans show Needs verification"
            : c.mode === "full_import" ? "full directory imported"
            : c.complete ? "all matching directory names checked"
            : c.in_progress ? "still loading this name in the background — more results may appear" : "check incomplete — results may be missing"}
        </div>
      ))}
      <div className="mt-1">Not searchable yet (their plans show Needs verification): {res.not_searchable.join(", ")}.</div>
    </div>
  );
}

function DoctorSearch({ base, zip0, initial, onPick, onCancel }) {
  const [q, setQ] = useState(initial || "");
  const [first, setFirst] = useState("");
  const [zip, setZip] = useState(zip0 || "");
  const [radius, setRadius] = useState(25);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (q.trim().length < 3 || zip.trim().length !== 5) { setRes(null); return undefined; }
    let live = true;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get(`${base}/search`, { params: { q: q.trim(), zip: zip.trim(), radius, first: first.trim() || undefined } });
        if (!live) return;
        setRes(data);
        if (data.in_progress && tick < 20) setTimeout(() => live && setTick((n) => n + 1), 6000);
      } catch { if (live) setRes({ ok: false }); } finally { if (live) setBusy(false); }
    }, 700);
    return () => { live = false; clearTimeout(t); };
  }, [q, first, zip, radius, base, tick]);
  return (
    <div className="mt-3 rounded-xl border border-[#E5E2DA] bg-white p-3" data-testid="mydoc-search">
      <div className="flex flex-wrap gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Doctor's last name" className="h-12 flex-1 min-w-[160px] text-base" data-testid="mydoc-search-input" />
        <Input value={first} onChange={(e) => setFirst(e.target.value)} placeholder="First name (optional)" className="h-12 flex-1 min-w-[140px] text-base" data-testid="mydoc-first-input" />
        <Input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="ZIP" className="h-12 w-24 text-base" data-testid="mydoc-zip-input" />
        <select value={radius} onChange={(e) => setRadius(Number(e.target.value))} aria-label="Search distance" className="h-12 rounded-md border px-2 text-base" data-testid="mydoc-radius-select">
          {RADII.map((r) => <option key={r} value={r}>{r} miles</option>)}
        </select>
      </div>
      {busy && <p className="mt-2 text-sm text-[#5B6670]" data-testid="mydoc-search-loading"><Loader2 className="w-4 h-4 inline animate-spin mr-1" />Searching…</p>}
      {res && res.ok === false && <p className="mt-2 text-sm text-[#5B6670]" data-testid="mydoc-search-error">Search isn't available right now. You can add the doctor manually below.</p>}
      {res?.ok && <Coverage res={res} />}
      {res?.ok && (
        <ul className="mt-2 space-y-1.5 max-h-72 overflow-y-auto" data-testid="mydoc-results">
          {res.results.length === 0 && <li className="text-sm text-[#5B6670]" data-testid="mydoc-no-results">No matching practice locations within {radius} miles in the connected directories. That isn't a coverage decision — widen the distance or add the doctor manually.</li>}
          {res.results.map((r) => (
            <li key={kOf(r)}>
              <button type="button" onClick={() => onPick(r)} className="w-full text-left rounded-lg border border-[#E5E2DA] p-2.5 hover:border-[#1E3E62]" data-testid={`mydoc-option-${r.location_key}`}>
                <div className="font-semibold text-[#0B192C]">{r.name}{r.npi ? <span className="font-normal text-xs text-[#8A8F98]"> · NPI {r.npi}</span> : null}</div>
                <div className="text-sm text-[#5B6670]">{(r.specialty || []).join(", ")}</div>
                <div className="text-sm text-[#5B6670]"><MapPin className="w-3.5 h-3.5 inline mr-0.5" />{r.location_name ? `${r.location_name} · ` : ""}{r.address} · {r.distance_miles} mi</div>
                <div className="text-[11px] text-[#8A8F98]">Listed by: {r.carriers.join(", ")}</div>
              </button>
            </li>
          ))}
        </ul>
      )}
      <button type="button" onClick={onCancel} className="mt-2 text-sm underline text-[#1E3E62]" data-testid="mydoc-search-cancel">Cancel</button>
    </div>
  );
}

function ManualDoctor({ onSave, onCancel }) {
  const [f, setF] = useState({ name: "", location_name: "", address: "", zip: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="mt-3 rounded-xl border border-[#E5E2DA] bg-white p-3 grid gap-2" data-testid="mydoc-manual-form">
      <Input value={f.name} onChange={set("name")} placeholder="Doctor's full name" className="h-12 text-base" data-testid="mydoc-manual-name" />
      <Input value={f.location_name} onChange={set("location_name")} placeholder="Practice name (optional)" className="h-12 text-base" data-testid="mydoc-manual-practice" />
      <Input value={f.address} onChange={set("address")} placeholder="Street address, city" className="h-12 text-base" data-testid="mydoc-manual-address" />
      <Input value={f.zip} onChange={set("zip")} placeholder="ZIP" className="h-12 w-28 text-base" data-testid="mydoc-manual-zip" />
      <p className="text-xs text-[#5B6670]">Doctors entered by hand show "Needs verification" for every plan until a licensed insurance agent confirms them.</p>
      <div className="flex gap-2">
        <Button type="button" disabled={!f.name.trim() || f.zip.trim().length !== 5} onClick={() => onSave({ ...f, manual: true })} className="h-11 rounded-full bg-[#0B192C] text-white" data-testid="mydoc-manual-save">Add doctor</Button>
        <Button type="button" variant="outline" onClick={onCancel} className="h-11 rounded-full" data-testid="mydoc-manual-cancel">Cancel</Button>
      </div>
    </div>
  );
}

function DoctorRow({ d, onRole, onMove, onRemove }) {
  return (
    <li className="rounded-xl border border-[#E5E2DA] bg-white p-3" data-testid={`mydoc-saved-${d.location_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-semibold text-[#0B192C]">{d.name}</div>
          <div className="text-sm text-[#5B6670]">{d.location_name ? `${d.location_name} · ` : ""}{d.address}{d.manual ? " · entered manually" : ""}</div>
          {d.entered_by?.source === "agent" && <div className="text-[11px] text-[#92400E]" data-testid={`mydoc-agent-${d.location_id}`}>Updated by agent {d.entered_by.name || ""} · {(d.entered_by.at || "").slice(0, 10)}</div>}
        </div>
        <select value={d.role} onChange={(e) => onRole(d, e.target.value)} aria-label="Doctor type" className="h-10 rounded-md border px-2 text-sm" data-testid={`mydoc-role-${d.location_id}`}>
          <option value="pcp">Primary care</option><option value="specialist">Specialist</option>
        </select>
      </div>
      <div className="mt-2 flex gap-3 text-sm">
        {!d.manual && <button type="button" onClick={() => onMove(d)} className="underline text-[#1E3E62]" data-testid={`mydoc-move-${d.location_id}`}><PencilLine className="w-3.5 h-3.5 inline mr-0.5" />Change location</button>}
        <button type="button" onClick={() => onRemove(d)} className="underline text-[#991B1B]" data-testid={`mydoc-remove-${d.location_id}`}><Trash2 className="w-3.5 h-3.5 inline mr-0.5" />Remove</button>
      </div>
    </li>
  );
}

function DoctorCell({ p, d }) {
  return (
    <li className="text-sm" data-testid={`mydoc-cell-${p.plan}-${d.location_key}`}>
      <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-semibold mr-1.5 ${CHIP[d.status] || CHIP.needs_verification}`} data-testid={`mydoc-status-${p.plan}-${d.location_key}`}>{d.label}</span>
      {d.name}
      {d.stale && <span className="ml-1 text-[11px] font-semibold text-[#B45309]" data-testid={`mydoc-stale-${p.plan}-${d.location_key}`}>Check expired — refreshing</span>}
      {d.status !== "in_network" && d.reason && <div className="text-[11px] text-[#6B7280] ml-1">{d.reason}{d.official_directory ? <> · <a href={d.official_directory} target="_blank" rel="noreferrer" className="underline">official directory</a></> : null}</div>}
    </li>
  );
}

function PlanMatrix({ matrix, onRequestAgent }) {
  return (
    <div className="mt-4" data-testid="mydoc-matrix">
      <h4 className="font-semibold text-[#0B192C]">Your doctors by plan ({matrix.year})</h4>
      <p className="text-xs text-[#5B6670]">Plans stay in their usual order. "Needs verification" means we couldn't confirm it yet — it does not mean the doctor is out of network.</p>
      <ul className="mt-2 space-y-2">
        {matrix.plans.map((p) => {
          const ok = p.doctors.filter((d) => d.status === "in_network");
          const open = p.doctors.filter((d) => d.status !== "in_network");
          return (
            <li key={p.plan} className="rounded-xl border border-[#E5E2DA] bg-white p-3" data-testid={`mydoc-plan-${p.plan}`}>
              <div className="font-semibold text-sm text-[#0B192C]">{p.plan_name || p.plan} <span className="font-normal text-[#8A8F98]">· {p.plan}</span></div>
              <div className={`mt-1.5 text-xs font-semibold ${ok.length > 0 ? "text-[#166534]" : "text-[#6B7280]"}`} data-testid={`mydoc-confirmed-${p.plan}`}>{ok.length > 0 ? `Confirmed in network: ${ok.length} of ${p.doctors.length}` : `No confirmed matches yet (0 of ${p.doctors.length})`}</div>
              {ok.length > 0 && <ul className="mt-1 space-y-1">{ok.map((d) => <DoctorCell key={`${d.doctor_key}|${d.location_key}`} p={p} d={d} />)}</ul>}
              {open.length > 0 && <div className="mt-1.5 text-xs font-semibold text-[#374151]" data-testid={`mydoc-unresolved-${p.plan}`}>Still to verify: {open.length}</div>}
              {open.length > 0 && <ul className="mt-1 space-y-1">{open.map((d) => <DoctorCell key={`${d.doctor_key}|${d.location_key}`} p={p} d={d} />)}</ul>}
            </li>
          );
        })}
      </ul>
      {onRequestAgent && <button type="button" onClick={onRequestAgent} className="mt-2 text-sm underline text-[#1E3E62]" data-testid="mydoc-ask-agent-btn">Ask a licensed insurance agent to confirm these doctors</button>}
    </div>
  );
}

export function MyDoctors({ plans, zip, token, isMember, onRequestAgent, baseOverride }) {
  const base = baseOverride || (isMember ? "/consumer/my-doctors" : `/public/ask/${token}/my-doctors`);
  const [docs, setDocs] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [mode, setMode] = useState(null);
  const [moving, setMoving] = useState(null);
  const [polls, setPolls] = useState(0);
  const planKey = plans.map((p) => `${p.contract_id}-${p.pbp}-${p.segment_id}`).join(",");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.post(`${base}/status`, { plans });
      setDocs(data.doctors || []); setMatrix(data.matrix);
      const running = (data.matrix?.plans || []).some((p) => p.doctors.some((d) => d.detail === "scan_in_progress"));
      if (running) setTimeout(() => setPolls((n) => (n < 15 ? n + 1 : n)), 8000);
    } catch { toast.error("Couldn't check your doctors right now."); }
  }, [base, planKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [refresh, polls]);

  const save = async (sel) => {
    try { await api.post(base, sel); setMode(null); toast.success(isMember ? "Doctor saved to your household." : "Doctor saved to this conversation."); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the doctor."); }
  };
  const remove = async (d, quiet) => {
    await api.delete(`${base}/${encodeURIComponent(d.practitioner_id)}/${encodeURIComponent(d.location_id)}`);
    if (!quiet) { toast.success("Doctor removed."); refresh(); }
  };
  const pick = async (r) => {
    if (moving) await remove(moving, true);
    await save({ ...r, role: moving?.role || (docs.length === 0 ? "pcp" : "specialist") });
    setMoving(null);
  };
  return (
    <section className="mt-5 rounded-2xl border border-[#E5E2DA] bg-[#FAF8F3] p-4" data-testid="my-doctors">
      <h3 className="font-semibold text-[#0B192C] flex items-center gap-2"><Stethoscope className="w-5 h-5" /> Your doctors</h3>
      <p className="text-xs text-[#5B6670] mt-1">Add your primary care doctor and specialists once. We check each practice location against every eligible plan.</p>
      <ul className="mt-3 space-y-2">{docs.map((d) => <DoctorRow key={kOf(d)} d={d} onRole={(x, role) => save({ ...x, doctor_key: x.practitioner_id, location_key: x.location_id, role })} onMove={(x) => { setMoving(x); setMode("search"); }} onRemove={(x) => remove(x)} />)}</ul>
      {!mode && docs.length >= 7 && <p className="mt-3 text-sm text-[#5B6670]" data-testid="mydoc-limit">You've added 7 doctors, the most we can check. Remove one to add another.</p>}
      {!mode && docs.length < 7 && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button type="button" onClick={() => setMode("search")} className="h-11 rounded-full bg-[#0B192C] text-white" data-testid="mydoc-add-btn"><Plus className="w-4 h-4 mr-1" />{docs.length ? "Add another doctor" : "Add a doctor"}</Button>
          <Button type="button" variant="outline" onClick={() => setMode("manual")} className="h-11 rounded-full" data-testid="mydoc-add-manual-btn">Can't find them? Add manually</Button>
        </div>
      )}
      {mode === "search" && <DoctorSearch base={base} zip0={zip} initial={moving ? (moving.family || "") : ""} onPick={pick} onCancel={() => { setMode(null); setMoving(null); }} />}
      {mode === "manual" && <ManualDoctor onSave={(f) => save({ ...f, role: docs.length === 0 ? "pcp" : "specialist" })} onCancel={() => setMode(null)} />}
      {matrix && docs.length > 0 && <PlanMatrix matrix={matrix} onRequestAgent={onRequestAgent} />}
    </section>
  );
}
